import { createAsyncThunk } from "@reduxjs/toolkit";

import * as productsAPI from "../../../api/products";
import { IProductID } from "../../../models/nominals";
import { IAPIPrice } from "../../../models/prices";

/**
 * Load the price for a specific product and quantity.
 *
 * This thunk fetches the current price for a product from the API, taking into account
 * the requested quantity. The price may be affected by:
 * - Active promotions and discounts
 * - Quantity-based pricing rules
 * - Product availability and stock status
 *
 * @param {Object} params - The parameters object
 * @param {IProductID} params.productID - The product to get pricing for
 * @param {number} params.quantity - The quantity being purchased
 *
 * @returns {Promise<IAPIPrice>} Promise resolving to the price data from the API
 *
 * @example
 * // Load price when user changes quantity
 * dispatch(loadPrice({
 *   productID: 123,
 *   quantity: 2
 * }));
 *
 * @sideEffects
 * - Updates `entities.price` with the fetched price via extraReducers
 */
export const loadPrice = createAsyncThunk(
    "configurator/loadPrice",
    async ({
        productID,
        quantity,
    }: {
        productID: IProductID;
        quantity: number;
    }): Promise<IAPIPrice> => {
        const productURL = productsAPI.getProductURL(productID);
        const price = await productsAPI.getProductPrice(productURL, quantity);
        return price;
    },
);
